import { useState } from "react";
import { ReportGapDialog } from "@/components/epics/report-gap-dialog";
import { useAcceptWrapUp, useEpic, useEpicWrapUp } from "@/hooks/queries/use-epics";
import { useFeatures } from "@/hooks/queries/use-features";
import { wrapUpFindings } from "@/lib/api/epics";
import { cn } from "@/lib/utils";

/**
 * Decide a build here instead of on the classic page. Shows what the final
 * check found, then: "It's fine, mark it done" (the owner accepts it; logged
 * as a decision and a line in the stream) or "Something's missing" (a
 * follow-up task and build, after which the check runs again).
 */

/** The finding lines worth showing: the summary line ("4 features look…") is dropped. */
export function decisionFindings(findings: string[]): string[] {
  return findings.filter((f) => !/^\d+ features? looks? only partly built\.?$/i.test(f.trim()));
}

export function BuildDecision({ epicId, onDecided }: { epicId: string; onDecided: () => void }) {
  const { data: view, isLoading } = useEpicWrapUp(epicId);
  const { data: epic } = useEpic(epicId);
  const { data: featurePages } = useFeatures({ epicId, limit: 100 });
  const accept = useAcceptWrapUp(epicId);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const findings = decisionFindings(wrapUpFindings(view?.wrapUp));
  const features = (featurePages?.pages.flatMap((p) => p.data) ?? []).map((f) => ({
    id: f.id,
    identifier: f.identifier,
    title: f.title,
  }));

  const markDone = async () => {
    setError(null);
    try {
      await accept.mutateAsync(note.trim() || undefined);
      onDecided();
    } catch {
      setError("Couldn't save that. Try again.");
    }
  };

  return (
    <div className="mt-1 flex flex-col gap-3">
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-muted" />
      ) : (
        findings.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              What the check found
            </h3>
            <ul className="mt-1.5 flex flex-col gap-1.5 text-sm text-foreground">
              {findings.map((f) => (
                <li key={f} className="break-words">
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Why it's fine (optional)</span>
        <textarea
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          maxLength={2000}
          rows={2}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </label>
      <button
        type="button"
        disabled={accept.isPending}
        onClick={() => {
          void markDone();
        }}
        className={cn(
          "inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          accept.isPending && "opacity-60"
        )}
      >
        {accept.isPending ? "Saving…" : "It's fine, mark it done"}
      </button>
      {epic && (
        <ReportGapDialog
          epic={epic}
          features={features}
          label="Something's missing"
          triggerClassName="min-h-11 w-full rounded-xl"
        />
      )}
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
